import * as React from "react";
import type { Location } from "react-router-dom";

/**
 * Standardized shape to pass previous location to routes that will redirect back
 */
type RedirectState = {
  from: Location;
};

/**
 * Custom type guard for location.state redirect object
 *
 * Make sure state is not null/undefined and it has a `from` key containing a Partial<Path> object
 */
const isRedirectState = (state: unknown): state is RedirectState =>
  !!state &&
  !!(state as RedirectState).from &&
  ("pathname" in (state as RedirectState).from ||
    "hash" in (state as RedirectState).from ||
    "search" in (state as RedirectState).from);

/**
 * Local storage key for `safeDynamicImport` refresh state
 */
const REFRESHED_KEY = "HUG_force_refreshed";

/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Lazy load module imports safely, refreshing on ChunkLoadErrors to get new version of chunk
 */
const safeDynamicImport = <T extends React.ComponentType<any>>(
  dynamicImport: () => Promise<{ default: T }>
) =>
  React.lazy(async () => {
    const hasBeenRefreshed =
      window.localStorage.getItem(REFRESHED_KEY) === "true";

    try {
      const module = await dynamicImport();

      window.localStorage.setItem(REFRESHED_KEY, "false");

      return module;
    } catch (error) {
      if (!hasBeenRefreshed) {
        // We haven't forced a refresh, assume they're not on latest version so refresh to get it
        window.localStorage.setItem(REFRESHED_KEY, "true");
        window.location.reload();
      }

      // We refreshed but are still getting an import error, assume they're on latest version
      // and raise whatever error we're seeing to the closest Error Boundary for logging
      throw error;
    }
  });
/* eslint-enable @typescript-eslint/no-explicit-any */

export type { RedirectState };
export { isRedirectState, safeDynamicImport };
